// Ícone e tom dos KPIs saem de `VOCABULARIO_KPI` (`src/auditoria/components/ds`).

import type { Dayjs } from 'dayjs';
import type { SxProps } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type { DsTone } from 'src/auditoria/components/ds';

import dayjs from 'dayjs';
import useSWR from 'swr';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { fetcher } from 'src/lib/axios';
import { DashboardContent } from 'src/layouts/dashboard/content';
import { TxDataGrid } from 'src/auditoria/components/tx-data-grid';
import {
  DsNum,
  DsField,
  DsButton,
  DsKpiCard,
  GradeCanvas,
  GradePager,
  DsPageHeader,
  DsIconButton,
  DsStatusChip,
  DsSearchField,
  SX_GRADE_CANVAS,
} from 'src/auditoria/components/ds';

import { isCancelada, formatCurrency, getSituacaoLabel, SITUACAO_EM_ANALISE } from '../Nfse/types';
import { type ServicoTomadoItem, type ServicosTomadosResponse } from './types';

// ----------------------------------------------------------------------
// Ponte de tipo: `TxDataGridProps.sx` é `SxProps` do @mui/system (tema `{}`),
// mais estreito que o `SxProps<Theme>` que o kit publica. Os valores são os
// mesmos — só o parâmetro de tema difere.
// ----------------------------------------------------------------------

const { sx: SX_GRADE, ...PROPS_GRADE } = SX_GRADE_CANVAS;
const SX_GRADE_COMPAT = SX_GRADE as SxProps;

// ----------------------------------------------------------------------
// Serviços Tomados - PF → Consultar (portal SN). Notas emitidas CONTRA o
// contribuinte (ele é o tomador); a grid mostra o EMISSOR/prestador. Ver
// docs/portal-contribuinte-migracao/SPEC-PORTAL-SN-TOMADOR-PF.md §3.
//
// Composição do artboard `portal-servicos-tomados.dc.html`: cabeçalho com CTA
// `.btnsec` → 4 KPIs (`.kpi-total` 28px/800, rótulo ACIMA, border-left de 3px)
// → card de filtros `.flabel` + `.fld`/`.searchbox` → casulo de grade com
// `.thead`/`.trow` e `.pager`. Cor sempre por token: o artboard escuro é o mesmo
// markup, só troca `data-color-scheme`.
// ----------------------------------------------------------------------

const SITUACAO_OPCOES = [
  { value: '', label: 'Todas' },
  { value: 1, label: 'Emitida (1)' },
  { value: 7, label: 'Cancelada — direta (7)' },
  { value: 3, label: 'Cancelada — formal (3)' },
  { value: 11, label: 'Em análise (11)' },
] as const;

/** O canvas mostra 4–6 linhas por página; §4.1 fixa 10 nas telas migradas. */
const LINHAS_POR_PAGINA = 10;

function formatCompetencia(value: string | null | undefined): string {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('MM/YYYY') : '—';
}

/**
 * Tom do `.chipsoft` por situação. `getSituacaoColor` devolve `LabelColor`, que é
 * mais largo que a união do kit (`DsTone`) — aqui a tradução é fechada.
 */
function tomDaSituacao(situacao: number): DsTone {
  if (isCancelada(situacao)) return 'error';
  if (situacao === SITUACAO_EM_ANALISE) return 'warning';
  return 'success';
}

export default function ServicosTomadosListPage() {
  const navigate = useNavigate();

  const [competencia, setCompetencia] = useState<Dayjs | null>(null);
  const [situacao, setSituacao] = useState<number | ''>('');
  const [pesquisa, setPesquisa] = useState('');
  const [pagina, setPagina] = useState(0);

  const swrKey = useMemo(() => {
    const params = new URLSearchParams();
    if (situacao !== '') params.set('situacao', String(situacao));
    if (competencia) {
      params.set('mes', String(competencia.month() + 1));
      params.set('ano', String(competencia.year()));
    }
    const qs = params.toString();
    return qs ? `/portal-api/servicos-tomados?${qs}` : '/portal-api/servicos-tomados';
  }, [situacao, competencia]);

  const { data, isLoading } = useSWR<ServicosTomadosResponse>(swrKey, fetcher);

  const todasNotas = useMemo(() => data?.notas ?? [], [data]);

  // Pesquisa livre sobre as notas já carregadas (o endpoint não recebe termo).
  const notas = useMemo(() => {
    const termo = pesquisa.trim().toLowerCase();
    if (!termo) return todasNotas;
    return todasNotas.filter((n) =>
      [n.numero, n.emissor?.nome, n.emissor?.cnpj]
        .filter(Boolean)
        .some((campo) => String(campo).toLowerCase().includes(termo))
    );
  }, [todasNotas, pesquisa]);

  // KPIs derivados do que a lista devolve (número, valor e situação). Aceite do
  // tomador e ISS retido não vêm neste endpoint — ver recibo do lote.
  const kpis = useMemo(() => {
    const soma = (itens: ServicoTomadoItem[]) =>
      itens.reduce((s, n) => s + Number(n.valor ?? 0), 0);
    const prestadores = new Set(
      notas.map((n) => n.emissor?.cnpj ?? n.emissor?.nome ?? '').filter(Boolean)
    );
    const emAnalise = notas.filter((n) => n.situacao === SITUACAO_EM_ANALISE);
    const canceladas = notas.filter((n) => isCancelada(n.situacao));
    return {
      recebidas: notas.length,
      total: soma(notas),
      prestadores: prestadores.size,
      emAnalise: { qtd: emAnalise.length, valor: soma(emAnalise) },
      canceladas: { qtd: canceladas.length, valor: soma(canceladas) },
    };
  }, [notas]);

  const totalPaginas = Math.max(1, Math.ceil(notas.length / LINHAS_POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas - 1);
  const linhasDaPagina = useMemo(
    () => notas.slice(paginaAtual * LINHAS_POR_PAGINA, (paginaAtual + 1) * LINHAS_POR_PAGINA),
    [notas, paginaAtual]
  );

  const rotuloCompetencia = competencia
    ? `competência ${competencia.format('MM/YYYY')}`
    : 'todas as competências';

  // Exportação em CSV do que está na tela (não há endpoint de exportação).
  const exportar = () => {
    const cabecalho = ['Número', 'Prestador', 'Competência', 'Valor', 'Situação'];
    const linhas = notas.map((n) => [
      n.numero ?? '',
      n.emissor?.nome ?? n.emissor?.cnpj ?? '',
      formatCompetencia(n.data_emissao),
      String(n.valor ?? 0),
      getSituacaoLabel(n.situacao),
    ]);
    const csv = [cabecalho, ...linhas]
      .map((linha) => linha.map((celula) => `"${String(celula).replace(/"/g, '""')}"`).join(';'))
      .join('\n');
    const url = URL.createObjectURL(
      new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' })
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = 'servicos-tomados.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const columns: GridColDef<ServicoTomadoItem>[] = [
    {
      field: 'numero',
      headerName: 'Número',
      width: 110,
      valueGetter: (_v, row) => row.numero ?? '—',
      renderCell: (params) => <DsNum mono>{params.row.numero ?? '—'}</DsNum>,
    },
    {
      field: 'emissor_nome',
      headerName: 'Prestador',
      flex: 1,
      minWidth: 200,
      valueGetter: (_v, row) => row.emissor?.nome ?? row.emissor?.cnpj ?? '—',
    },
    {
      field: 'data_emissao',
      headerName: 'Competência',
      width: 120,
      valueGetter: (_v, row) => formatCompetencia(row.data_emissao),
      renderCell: (params) => <DsNum mono>{formatCompetencia(params.row.data_emissao)}</DsNum>,
    },
    {
      field: 'valor',
      headerName: 'Valor (R$)',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_v, row) => Number(row.valor ?? 0),
      renderCell: (params) => (
        <DsNum mono align="right" sx={{ fontWeight: 700 }}>
          {formatCurrency(params.row.valor)}
        </DsNum>
      ),
    },
    {
      field: 'situacao',
      headerName: 'Situação',
      width: 140,
      renderCell: (params) => (
        <DsStatusChip tone={tomDaSituacao(params.row.situacao)}>
          {getSituacaoLabel(params.row.situacao)}
        </DsStatusChip>
      ),
    },
    {
      field: 'abrir',
      headerName: '',
      width: 56,
      align: 'right',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <DsIconButton
          icon="chevron-dir"
          aria-label={`Abrir a nota ${params.row.numero ?? params.row.id}`}
          onClick={() => navigate(`/servicos-tomados/${params.row.id}`)}
        />
      ),
    },
  ];

  return (
    <DashboardContent>
      <DsPageHeader
        title="Serviços Tomados"
        weight={800}
        subtitle="Notas recebidas de prestadores · competência, situação e retenções."
        action={
          <DsButton tone="neutral" size="tall" disabled={notas.length === 0} onClick={exportar}>
            Exportar
          </DsButton>
        }
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: '12px',
          mb: '12px',
        }}
      >
        <DsKpiCard
          order="title-first"
          scale="sans28"
          title="Notas recebidas"
          total={<DsNum>{kpis.recebidas}</DsNum>}
          sub={rotuloCompetencia}
          conceito="notas"
          borderLeft
        />
        <DsKpiCard
          order="title-first"
          scale="sans28"
          title="Total tomado"
          total={<DsNum>{formatCurrency(kpis.total)}</DsNum>}
          sub={`${kpis.prestadores} prestador(es)`}
          conceito="dinheiro-neutro"
          borderLeft
        />
        {/* O card é o próprio filtro de situação da tela: clicar aplica
            "Em análise (11)" no seletor e volta para a primeira página. */}
        <DsKpiCard
          order="title-first"
          scale="sans28"
          title="Em análise"
          total={<DsNum>{kpis.emAnalise.qtd}</DsNum>}
          sub={formatCurrency(kpis.emAnalise.valor)}
          conceito="aguardando"
          borderLeft
          onClick={() => {
            setSituacao(SITUACAO_EM_ANALISE);
            setPagina(0);
          }}
        />
        <DsKpiCard
          order="title-first"
          scale="sans28"
          title="Canceladas"
          total={<DsNum>{kpis.canceladas.qtd}</DsNum>}
          sub={formatCurrency(kpis.canceladas.valor)}
          conceito="cancelado"
          borderLeft
        />
      </Box>

      <Card sx={{ padding: '16px 18px', mb: '12px' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { md: 'flex-end' },
            gap: '10px',
          }}
        >
          <DsField label="Competência" density="sm" sx={{ flex: 1 }}>
            <DatePicker
              views={['year', 'month']}
              openTo="month"
              format="MM/YYYY"
              value={competencia}
              onChange={(v) => {
                setCompetencia(v);
                setPagina(0);
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  placeholder: 'Todas as competências',
                },
                field: { clearable: true },
              }}
            />
          </DsField>

          <DsField label="Situação" density="sm" sx={{ flex: 1 }}>
            <TextField
              select
              fullWidth
              value={situacao}
              onChange={(e) => {
                setSituacao(e.target.value === '' ? '' : Number(e.target.value));
                setPagina(0);
              }}
            >
              {SITUACAO_OPCOES.map((opt) => (
                <MenuItem key={String(opt.value)} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </DsField>

          <DsField label="Buscar" density="sm" sx={{ flex: 2 }}>
            <DsSearchField
              size="sm"
              value={pesquisa}
              onChange={(v) => {
                setPesquisa(v);
                setPagina(0);
              }}
              placeholder="Número, prestador ou CPF/CNPJ"
              sx={{ minWidth: 0 }}
            />
          </DsField>
        </Box>
      </Card>

      <GradeCanvas
        pager={
          <GradePager
            de={notas.length === 0 ? 0 : paginaAtual * LINHAS_POR_PAGINA + 1}
            ate={Math.min((paginaAtual + 1) * LINHAS_POR_PAGINA, notas.length)}
            total={notas.length}
            label={`Página ${paginaAtual + 1} de ${totalPaginas}`}
            onPrev={() => setPagina((p) => Math.max(0, p - 1))}
            onNext={() => setPagina((p) => p + 1)}
            prevDisabled={paginaAtual === 0}
            nextDisabled={paginaAtual + 1 >= totalPaginas}
          />
        }
      >
        <TxDataGrid<ServicoTomadoItem>
          rows={linhasDaPagina}
          columns={columns}
          loading={isLoading}
          emptyLabel="Nenhuma nota recebida"
          autoHeight
          hideFooter
          {...PROPS_GRADE}
          sx={SX_GRADE_COMPAT}
        />
      </GradeCanvas>
    </DashboardContent>
  );
}
